// Social platforms (docs/product-decisions.md 2026-10-31). An account a person
// grows, an X or TikTok profile, can be a product. zooi never reads these
// platforms' pages: their terms do not allow it, whatever the page is (a
// profile, a post, a company page or a short link), and a person can say what
// the page would. The hosts decide that; the profile only names the product.
// X's official API with the person's own token, and its public oEmbed endpoint,
// are not those pages (docs/product-decisions.md 2026-11-03). Neither is asking
// twitterapi.io, a service the person chose, with their own key (same date).

export const SOCIAL_PLATFORMS = ["x", "tiktok", "youtube", "instagram", "threads", "bluesky", "linkedin", "xiaohongshu", "bilibili", "weibo", "douyin", "facebook"] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

/** Each platform's hosts, short links included; a subdomain of one belongs to it too. */
export const SOCIAL_HOSTS: Readonly<Record<SocialPlatform, readonly string[]>> = {
  x: ["x.com", "twitter.com", "t.co"],
  tiktok: ["tiktok.com"],
  youtube: ["youtube.com", "youtu.be"],
  instagram: ["instagram.com", "instagr.am"],
  threads: ["threads.net", "threads.com"],
  bluesky: ["bsky.app"],
  linkedin: ["linkedin.com", "lnkd.in"],
  xiaohongshu: ["xiaohongshu.com", "xhslink.com"],
  bilibili: ["bilibili.com", "b23.tv"],
  weibo: ["weibo.com", "weibo.cn"],
  douyin: ["douyin.com", "iesdouyin.com"],
  facebook: ["facebook.com", "fb.com", "fb.me", "fb.watch"],
};

const hostOf = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return parsed.hostname.toLowerCase().replace(/\.$/, "");
  } catch { return null; }
};

/** The social platform a URL belongs to, by its host, or null. */
export function socialPlatformOf(url: string): SocialPlatform | null {
  const host = hostOf(url);
  if (!host) return null;
  for (const platform of SOCIAL_PLATFORMS) if (SOCIAL_HOSTS[platform].some((h) => host === h || host.endsWith(`.${h}`))) return platform;
  return null;
}

/** Whether zooi must not read this URL: any page of a social platform. */
export const isSocialHost = (url: string) => socialPlatformOf(url) !== null;

const PROFILE_PATHS: [SocialPlatform, RegExp, RegExp][] = [
  ["x", /^(x|twitter)\.com$/, /^\/([A-Za-z0-9_]{1,15})\/?$/],
  ["tiktok", /^tiktok\.com$/, /^\/@([A-Za-z0-9_.]{2,24})\/?$/],
  ["youtube", /^youtube\.com$/, /^\/(@[A-Za-z0-9_.-]{3,30}|c\/[^/]+|channel\/[A-Za-z0-9_-]+)\/?$/],
  ["instagram", /^instagram\.com$/, /^\/([A-Za-z0-9_.]{1,30})\/?$/],
  ["threads", /^threads\.(net|com)$/, /^\/@([A-Za-z0-9_.]{1,30})\/?$/],
  ["bluesky", /^bsky\.app$/, /^\/profile\/([A-Za-z0-9.-]{3,253})\/?$/],
  ["linkedin", /^linkedin\.com$/, /^\/in\/([A-Za-z0-9-]{3,100})\/?$/],
  ["xiaohongshu", /^xiaohongshu\.com$/, /^\/user\/profile\/([a-f0-9]{8,32})\/?$/],
  ["bilibili", /^space\.bilibili\.com$/, /^\/(\d{1,20})\/?$/],
  ["weibo", /^weibo\.com$/, /^\/(u\/\d{5,20}|[A-Za-z0-9_]{4,30})\/?$/],
  ["douyin", /^douyin\.com$/, /^\/user\/([A-Za-z0-9_-]{8,80})\/?$/],
  ["facebook", /^facebook\.com$/, /^\/([A-Za-z0-9.]{5,50})\/?$/],
];
/** Paths on those hosts that are pages of the platform, not someone's profile. */
const NOT_PROFILES = new Set([
  "home", "explore", "search", "login", "signup", "settings", "i", "intent", "share", "about", "help", "privacy", "terms", "tos", "watch", "results", "feed", "reels", "p", "messages", "notifications",
  "jobs", "legal", "download", "business", "creators", "trending", "shorts", "live", "music", "gaming", "premium", "stories", "tv", "discover", "following", "foryou", "tag", "hashtag", "compose", "account", "accounts", "directory", "policies", "groups", "pages", "marketplace", "events",
]);

/** The account a URL names, when it is a person's or brand's profile on a social platform. */
export function socialProfile(url: string): { platform: SocialPlatform; handle: string } | null {
  const host = hostOf(url)?.replace(/^(www|m|mobile)\./, "");
  if (!host || !isSocialHost(url)) return null;
  for (const [platform, hostPattern, pathPattern] of PROFILE_PATHS) {
    if (!hostPattern.test(host)) continue;
    const match = pathPattern.exec(new URL(url).pathname);
    if (!match) return null;
    const handle = match[1].replace(/^@/, "");
    return NOT_PROFILES.has(handle.toLowerCase()) ? null : { platform, handle };
  }
  return null;
}

/**
 * A product that is only an account a person grows: a social profile with no
 * repository. zooi has nothing of its own to read for it, so the person says
 * what it is and what to watch, and numbers from a site of its own do not
 * apply (docs/product-decisions.md 2026-11-03).
 */
export const isAccountOnly = (product: { url?: string | null; repository?: string | null }) =>
  Boolean(product.url && !product.repository && socialProfile(product.url));

/** An X handle without the @, or null when it cannot be one. */
export function xHandle(input: string): string | null {
  const handle = input.trim().replace(/^@/, "");
  return /^[A-Za-z0-9_]{1,15}$/.test(handle) ? handle : null;
}

/**
 * How zooi reads a connected X account (docs/product-decisions.md 2026-11-03). `official` is X's own API;
 * `twitterapi_io` is an independent service the person chose and pays for, and it reads only the follower total.
 * A connection made before there was a choice names no reader.
 */
export const X_READERS = ["official", "twitterapi_io"] as const;
export type XReader = (typeof X_READERS)[number];
export const xReaderOf = (config: unknown): XReader => (config as { reader?: unknown } | null | undefined)?.reader === "twitterapi_io" ? "twitterapi_io" : "official";

/** What one read of /twitter/user/info costs through twitterapi.io, at its prices on `prices_checked_on`. */
export const TWITTERAPI_READ_RULES = { credits_per_read: 18, credits_per_usd: 100_000, prices_checked_on: "2026-11-03" } as const;

/**
 * What zooi reads with a person's X token, so the most it can cost is known: one account read and at most
 * `own_posts` of their posts a day, and with search on `search_queries` searches of `search_posts` posts and
 * their authors. At X's prices on 2026-11-03 (docs/product-decisions.md) that is at most `max_daily_usd`.
 */
export const X_READ_RULES = {
  own_posts: 100,
  /** Own posts read back this far each day, or to the last read when one was missed, at most `lookback_max_days`. */
  lookback_hours: 48,
  lookback_max_days: 7,
  search_queries: 2,
  search_posts: 15,
  search_every_hours: 24,
  /** A reply marked posted and still not among the person's posts on a read this long after is reported. */
  match_after_hours: 1,
  max_daily_usd: 0.6,
  max_daily_usd_without_search: 0.11,
  prices_checked_on: "2026-11-03",
} as const;
